import React, { useEffect, useState } from 'react'
import './EducationInfo.scss'
import profileService from 'Services/profileService'
import { ModalWrapper } from 'Components'
import BankIcon from 'Assets/Images/bank.png'
import HashIcon from 'Assets/Images/hash.png'
import NyscIcon from 'Assets/Icons/nysc.png'
import DesignerIcon from 'Assets/Icons/designer.png'
import LocationIcon from 'Assets/Icons/location.png'
import ProfileIcon from 'Assets/Icons/profile.png'
import AddIcon from 'Assets/Icons/add.png'
import EditIcon from 'Assets/Icons/edit-2.png'
import EducationUpdate from './Modals/EducationUpdate/EducationUpdate'
import NyscUpdate from './Modals/NyscUpdate/NyscUpdate'
import WorkUpdate from './Modals/WorkUpdate/WorkUpdate'
import ReferenceUpdate from './Modals/ReferenceUpdate/ReferenceUpdate'
import { EmployeeSingleType } from 'Models/employeeType'
import Swal from 'sweetalert2'

type EducationType = {
  institutionName: string
  certificateEarned: string
  yearOfGraduation: string
  location: string
}
type NyscType = {
  hasCompletedNYSC: boolean
  certificateNumber: string
  placeOfPrimaryAssignment: string
  institutionAddress: string
}
export type WorkType = {
  companyName: string
  role: string
  duration: {
    endYear: string
    startYear: string
  }
  companyContactName: string
  officialEmail: string
  officialPhoneNumber: string
}
type RefType = {
  fullName: string
  occupation: string
  phoneNumber: string
  emailAddress: string
  address: string
}

type ModalType = 'education' | 'nysc' | 'work' | 'ref'

type Props = {
  profile: EmployeeSingleType | null | undefined
  setProfile: any
}

const EducationInfo = ({ profile, setProfile }: Props) => {
  const [modalType, setModalType] = useState<ModalType | null>(null)

  const closeModal = () => {
    setModalType(null)
  }

  const getEducation = (profile: any): EducationType[] => {
    return profile?.educationHistory || []
  }
  const [editingIndex, setEditingIndex] = useState(null)
  const [selectedEducation, setSelectedEducation] = useState<EducationType | null>(null)
  const [education, setEducation] = useState<EducationType[]>(() => getEducation(profile))

  useEffect(() => {
    setEducation(getEducation(profile))
  }, [profile])

  const handleAdd = (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setEditingIndex(null)
    setSelectedEducation(null)
    setModalType('education')
  }

  const handleEdit = (index: any) => {
    setEditingIndex(index)
    setSelectedEducation(education[index])
    setModalType('education')
  }

  const handleSaveEducation = async (data: EducationType) => {
    const formatted = {
      institutionName: data.institutionName,
      certificateEarned: data.certificateEarned,
      yearOfGraduation: data.yearOfGraduation,
      location: data.location,
    }

    const updatedEducation =
      editingIndex !== null
        ? education.map((item, i) => (i === editingIndex ? formatted : item))
        : [...education, formatted]

    await profileService
      .updateProfileV2({
        educationHistory: updatedEducation,
      })
      .then((res: any) => {
        // setRequest(false)

        if (res.data.status === true) {
          Swal.fire({
            text: 'Education information updated successfully',
            icon: 'success',
            confirmButtonText: 'Ok',
          })

          setEducation(updatedEducation)
          // refetchProfile()

          setModalType(null)
        } else {
          Swal.fire({
            title: 'Oops!',
            text: 'Something went wrong!',
            icon: 'warning',
            confirmButtonText: 'Close',
          })
        }
      })
      .catch((error: any) => {
        // setRequest(false)

        Swal.fire({
          title: 'Oops!',
          text: error?.response?.data?.error || 'Something went wrong',
          icon: 'warning',
          confirmButtonText: 'Close',
        })
      })
  }

  const getNysc = (profile: any): NyscType => {
    return (
      profile?.nyscDetails || {
        hasCompletedNYSC: false,
        certificateNumber: '',
        placeOfPrimaryAssignment: '',
        institutionAddress: '',
      }
    )
  }

  const [nysc, setNysc] = useState<NyscType>(() => getNysc(profile))

  useEffect(() => {
    setNysc(getNysc(profile))
  }, [profile])

  const handleEditNysc = () => {
    setModalType('nysc')
  }

  const handleSaveNysc = async (data: NyscType) => {
    const formatted = {
      hasCompletedNYSC: data.hasCompletedNYSC,
      certificateNumber: data.certificateNumber,
      placeOfPrimaryAssignment: data.placeOfPrimaryAssignment,
      institutionAddress: data.institutionAddress,
    }
    await profileService
      .updateProfileV2({
        nyscDetails: formatted,
      })
      .then((res: any) => {
        if (res.data.status === true) {
          Swal.fire({
            text: 'Nysc information updated successfully',
            icon: 'success',
            confirmButtonText: 'Ok',
          })
          setNysc(formatted)
          // refetchProfile()

          setModalType(null)
        } else {
          Swal.fire({
            title: 'Oops!',
            text: 'Something went wrong!',
            icon: 'warning',
            confirmButtonText: 'Close',
          })
        }
      })
      .catch((error: any) => {
        Swal.fire({
          title: 'Oops!',
          text: error?.response?.data?.error || 'Something went wrong',
          icon: 'warning',
          confirmButtonText: 'Close',
        })
      })
  }

  const getWork = (profile: any): WorkType[] => {
    return profile?.workExperience || []
  }

  const [editingWorkIndex, setEditingWorkIndex] = useState(null)
  const [selectedWork, setSelectedWork] = useState<WorkType | null>(null)
  const [work, setWork] = useState<WorkType[]>(() => getWork(profile))

  useEffect(() => {
    setWork(getWork(profile))
  }, [profile])

  const handleWorkAdd = (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setEditingWorkIndex(null)
    setSelectedWork(null)
    setModalType('work')
  }

  const handleWorkEdit = (index: any) => {
    setEditingWorkIndex(index)
    setSelectedWork(work[index])
    setModalType('work')
  }

  const handleSaveWork = async (data: WorkType) => {
    const formatted = {
      companyName: data.companyName,
      role: data.role,
      duration: {
        endYear: String(data.duration.endYear),
        startYear: String(data.duration.startYear),
      },
      companyContactName: data.companyContactName,
      officialEmail: data.officialEmail,
      officialPhoneNumber: data.officialPhoneNumber,
    }

    const updatedEducation =
      editingIndex !== null
        ? work.map((item, i) => (i === editingIndex ? formatted : item))
        : [...work, formatted]

    await profileService
      .updateProfileV2({
        workExperience: updatedEducation,
      })
      .then((res: any) => {
        // setRequest(false)

        if (res.data.status === true) {
          Swal.fire({
            text: 'Education information updated successfully',
            icon: 'success',
            confirmButtonText: 'Ok',
          })

          setWork(updatedEducation)
          // refetchProfile()
        } else {
          Swal.fire({
            title: 'Oops!',
            text: 'Something went wrong!',
            icon: 'warning',
            confirmButtonText: 'Close',
          })
        }
      })
      .catch((error: any) => {
        // setRequest(false)

        Swal.fire({
          title: 'Oops!',
          text: error?.response?.data?.error || 'Something went wrong',
          icon: 'warning',
          confirmButtonText: 'Close',
        })
      })
  }

  const getRef = (profile: any): RefType[] => {
    return profile?.characterReferences || []
  }

  const [editingRefIndex, setEditingRefIndex] = useState(null)
  const [selectedRef, setSelectedRef] = useState<RefType | null>(null)
  const [ref, setRef] = useState<RefType[]>(() => getRef(profile))

  useEffect(() => {
    setRef(getRef(profile))
  }, [profile])

  const handleRefAdd = () => {
    setEditingRefIndex(null)
    setSelectedRef(null)
    setModalType('ref')
  }

  const handleRefEdit = (index: any) => {
    setEditingRefIndex(index)
    setSelectedRef(ref[index])
    setModalType('ref')
  }

  const handleSaveRef = async (data: RefType) => {
    const formatted = {
      fullName: data.fullName,
      occupation: data.occupation,
      phoneNumber: data.phoneNumber,
      emailAddress: data.emailAddress,
      address: data.address,
    }

    const updatedEducation =
      editingRefIndex !== null
        ? ref.map((item, i) => (i === editingRefIndex ? formatted : item))
        : [...ref, formatted]

    await profileService
      .updateProfileV2({
        characterReferences: updatedEducation,
      })
      .then((res: any) => {
        // setRequest(false)

        if (res.data.status === true) {
          Swal.fire({
            text: 'References information updated successfully',
            icon: 'success',
            confirmButtonText: 'Ok',
          })

          setRef(updatedEducation)
          // refetchProfile()
        } else {
          Swal.fire({
            title: 'Oops!',
            text: 'Something went wrong!',
            icon: 'warning',
            confirmButtonText: 'Close',
          })
        }
      })
      .catch((error: any) => {
        // setRequest(false)

        Swal.fire({
          title: 'Oops!',
          text: error?.response?.data?.error || 'Something went wrong',
          icon: 'warning',
          confirmButtonText: 'Close',
        })
      })
  }

  return (
    <div className='education-info'>
      {/* <form> */}
      <div className='container-fluid mt-4 p-0 statutory'>
        <div className='row g-4'>
          <div className='col-lg-7 d-flex flex-column gap-4'>
            <form>
              <div className='card'>
                <div className='card-body mt-2'>
                  <div className='bank-details-h line d-flex justify-content-between align-items-center'>
                    <h6 className='heading'>Education History</h6>
                    <button onClick={handleAdd} className='reset-btn-as-link'>
                      <img src={AddIcon} alt='icon' />
                    </button>
                  </div>
                  {education.length === 0 ? (
                    <div className='sub-not-con'>
                      {' '}
                      <h6 className='sub-not'>Not Provided</h6>
                    </div>
                  ) : (
                    education.map((item, index) => (
                      <div className='bank-details line' key={index}>
                        <div className='icon-head'>
                          <div className='first'>
                            <img src={BankIcon} alt='' />
                            <h6>{item.institutionName}</h6>
                          </div>

                          <button
                            type='button'
                            className='reset-btn-as-link'
                            onClick={() => handleEdit(index)}
                          >
                            <img src={EditIcon} alt='icon' />
                          </button>
                        </div>
                        <h6 className='sub'>{item.certificateEarned}</h6>
                        <span className='text-muted'>Graduated:</span>{' '}
                        <span className='text-muted'>{item.yearOfGraduation}</span>
                        <h6 className='sub pt-2'>{item.location}</h6>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </form>

            <div className='card'>
              <div className='card-body mt-2'>
                <div className='bank-details-h line d-flex justify-content-between align-items-center'>
                  <h6 className='heading'>NYSC Information</h6>
                  <button onClick={handleEditNysc} className='reset-btn-as-link'>
                    <img src={EditIcon} alt='icon' />
                  </button>
                </div>
                <div className='bank-details'>
                  <div className='icon-head nysc2'>
                    <div className='first'>
                      <img src={NyscIcon} alt='' />
                      <div className='nyscCompleted'>
                        <span className='text-muted'>NYSC Completed?</span>{' '}
                        <span
                          className={`${nysc?.hasCompletedNYSC === false ? 'sub-not' : 'sub'} `}
                        >
                          {nysc.hasCompletedNYSC === false ? 'Not Provided' : 'Yes'}
                        </span>
                      </div>
                    </div>

                    <button
                      type='button'
                      className='d-none reset-btn-as-link'
                      onClick={handleEditNysc}
                    >
                      <img src={EditIcon} alt='icon' />
                    </button>
                  </div>
                  <div className='second nysc2'>
                    <div className='nyscCompleted d-flex gap-2'>
                      <img src={HashIcon} alt='icon' />
                      <span className='text-muted'>NYSC Certificate Number</span>{' '}
                    </div>
                    <p className={`mt-1 ${nysc?.certificateNumber === '' ? 'sub-not' : 'sub'} `}>
                      {nysc.certificateNumber === '' ? 'Not Provided' : nysc.certificateNumber}
                    </p>
                  </div>
                  <div className='second'>
                    <div className='nyscCompleted d-flex gap-2'>
                      <img src={DesignerIcon} alt='icon' />
                      <span className='text-muted'>Place of Primary Assignment (PPA)</span>{' '}
                    </div>
                    <p
                      className={`mt-1 ${nysc?.placeOfPrimaryAssignment === '' ? 'sub-not' : 'sub'} `}
                    >
                      {nysc.placeOfPrimaryAssignment === ''
                        ? 'Not Provided'
                        : nysc.placeOfPrimaryAssignment}
                    </p>
                  </div>
                  <div className='second nysc3'>
                    <div className='nyscCompleted d-flex gap-2'>
                      <img src={LocationIcon} alt='icon' />
                      <span className='text-muted'>Institution Address</span>{' '}
                    </div>
                    <p className={`mt-1 ${nysc?.institutionAddress === '' ? 'sub-not' : 'sub'} `}>
                      {nysc.institutionAddress === '' ? 'Not Provided' : nysc.institutionAddress}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className='col-lg-5 d-flex flex-column gap-4'>
            <div className='card'>
              <div className='card-body mt-2'>
                <div className='bank-details-h line d-flex justify-content-between align-items-center'>
                  <h6 className='heading'>Work Experience</h6>
                  <button onClick={handleWorkAdd} className='reset-btn-as-link'>
                    <img src={AddIcon} alt='icon' />
                  </button>
                </div>
                {work.length === 0 ? (
                  <div className='sub-not-con'>
                    {' '}
                    <h6 className='sub-not'>Not Provided</h6>
                  </div>
                ) : (
                  work.map((item, index) => (
                    <div className='work-exp line' key={index}>
                      <div className='icon-head'>
                        <div className='first'>
                          <img src={DesignerIcon} alt='' />
                          <h6>{item.companyName}</h6>
                        </div>

                        <div className='first'>
                          <h6 className='text-muted'>
                            {item.duration.startYear} – {item.duration.endYear}
                          </h6>
                          <button
                            type='button'
                            className='reset-btn-as-link'
                            onClick={() => handleWorkEdit(index)}
                          >
                            <img src={EditIcon} alt='icon' />
                          </button>
                        </div>
                      </div>
                      <h6 className='sub'>{item.role}</h6>

                      <div className='others mb-2'>
                        <div className='group'>
                          <span className='text-muted'>HR / Owner Name</span>{' '}
                          <span className='text-unmuted'>{item.companyContactName}</span>
                        </div>
                        <div className='group'>
                          <span className='text-muted'>Official Email</span>{' '}
                          <span className='text-unmuted'>{item.officialEmail}</span>
                        </div>
                        <div className='group'>
                          <span className='text-muted'>Official Phone Number</span>{' '}
                          <span className='text-unmuted'>{item.officialPhoneNumber}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className='card'>
              <div className='card-body mt-2'>
                <div className='bank-details-h line d-flex justify-content-between align-items-center'>
                  <h6 className='heading'>Character References</h6>
                  <button onClick={handleRefAdd} className='reset-btn-as-link'>
                    <img src={AddIcon} alt='icon' />
                  </button>
                </div>
                {ref.length === 0 ? (
                  <div className='sub-not-con'>
                    {' '}
                    <h6 className='sub-not'>Not Provided</h6>
                  </div>
                ) : (
                  ref.map((item, index) => (
                    <div className={`work-exp ${ref.length > 1 && 'line'}`} key={index}>
                      <div className='icon-head'>
                        <div className='first'>
                          <img src={ProfileIcon} alt='' />
                          <h6>{item.fullName}</h6>
                        </div>

                        <div className='first'>
                          <button
                            type='button'
                            className='reset-btn-as-link'
                            onClick={() => handleRefEdit(index)}
                          >
                            <img src={EditIcon} alt='icon' />
                          </button>
                        </div>
                      </div>
                      <h6 className='sub'>{item.occupation}</h6>

                      <div className='others mb-2'>
                        <div className='group'>
                          <span className='text-muted'>Phone Number</span>{' '}
                          <span className='text-unmuted'>{item.phoneNumber}</span>
                        </div>
                        <div className='group'>
                          <span className='text-muted'>Email Address</span>{' '}
                          <span className='text-unmuted'>{item.emailAddress}</span>
                        </div>
                        <div className='group'>
                          <span className='text-muted'>Address</span>{' '}
                          <span className='text-unmuted'>{item.address}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ModalWrapper
        showModal={modalType !== null}
        closeModal={closeModal}
        title={
          <span className='custom-modal-title'>
            {modalType === 'education'
              ? editingIndex !== null
                ? 'Edit Education'
                : 'Add Education History'
              : modalType === 'nysc'
                ? nysc
                  ? 'Edit NYSC Information'
                  : 'Add NYSC Information'
                : modalType === 'work'
                  ? editingWorkIndex !== null
                    ? 'Edit Work Experience'
                    : 'Add Work Experience'
                  : modalType === 'ref'
                    ? editingRefIndex !== null
                      ? 'Edit Characterr Reference'
                      : 'Add Character Reference'
                    : ''}
          </span>
        }
      >
        {modalType === 'education' && (
          <EducationUpdate
            toggleModal={closeModal}
            selectedEducation={selectedEducation}
            onSave={handleSaveEducation}
          />
        )}

        {modalType === 'nysc' && (
          <NyscUpdate
            toggleModal={closeModal}
            nysc={nysc}
            setNysc={setNysc}
            onSave={handleSaveNysc}
          />
        )}

        {modalType === 'work' && (
          <WorkUpdate
            toggleModal={closeModal}
            selectedWork={selectedWork}
            onSave={handleSaveWork}
          />
        )}

        {modalType === 'ref' && (
          <ReferenceUpdate
            toggleModal={closeModal}
            selectedRef={selectedRef}
            onSave={handleSaveRef}
          />
        )}
      </ModalWrapper>
    </div>
  )
}

export default EducationInfo
